/**
 * Readiness pill for one submission pack row.
 *
 * The state shown is `item.readiness` — the EFFECTIVE verdict (a manual status
 * wins, otherwise the server-derived one). `item.derived` is display detail and
 * never decides the colour here. The words and colour come from
 * `packReadinessLabel` (`@/lib/pack-readiness`), which the checklist export
 * shares, so the pill and the spreadsheet cannot disagree.
 */

import { Badge } from '@/components/ui/badge'
import type { StatusSource, SubmissionStatus, TriageClass } from '@/lib/api'
import { packReadinessLabel } from '@/lib/pack-readiness'

interface PackReadinessPillProps {
  readiness: SubmissionStatus
  /** null folds into 'attachment' — the same fallback the grouping uses. */
  triageClass: TriageClass | null
  hasLink: boolean
  statusSource: StatusSource
  /** The pack's submission deadline. `null` never escalates to "Missing". */
  deadline: string | null
  /** Injected for tests; defaults to the current time. */
  now?: Date
  /**
   * Renders "set manually" beside the pill for a manual status. The drawer
   * opts in — there it explains the "Use the derived status" menu entry. Rows
   * leave it off: repeated down a 75-row list it was pure noise (2026-08-29).
   */
  showSource?: boolean
}

export function PackReadinessPill({
  readiness,
  triageClass,
  hasLink,
  statusSource,
  deadline,
  now,
  showSource = false,
}: PackReadinessPillProps) {
  const { label, variant } = packReadinessLabel(
    readiness,
    triageClass,
    hasLink,
    deadline,
    now ?? new Date()
  )

  return (
    <span className="inline-flex items-center gap-1.5">
      <Badge variant={variant} dot className="whitespace-nowrap">
        {label}
      </Badge>
      {showSource && statusSource === 'manual' && (
        <span className="text-meta whitespace-nowrap">set manually</span>
      )}
    </span>
  )
}
